import { useCallback, useEffect, useState } from "react";
import { api, extractAxiosError } from "@/lib/api";
import { DeleteConfirmModal } from "@/components/DeleteConfirmModal";
import { AdminPanelShell } from "@/components/admin/AdminPanelShell";
import { Dialog, DialogFooter } from "@/components/EditPercentagesDialog";
import { Trash2, ChevronDown, ChevronUp, Users, Loader2, Crown, UserMinus, Layers } from "lucide-react";
import { toast } from "sonner";

const PLAN_BADGE = {
    free: "bg-white/[0.06] text-white/55 border-white/10",
    pro: "bg-[#00BF0D]/10 text-[#00BF0D] border-[#00BF0D]/25",
    business: "bg-[#5D00FF]/15 text-[#B794FF] border-[#5D00FF]/30",
};

const DETAIL_PANEL_H = "max-h-80";

function formatWhen(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch (_e) {
        return iso;
    }
}

function formatOrgId(id, { compact = false } = {}) {
    if (!id) return "—";
    if (!compact || id.length <= 12) return id;
    return `…${id.slice(-8)}`;
}

function OrgDetailPanel({ orgId, expanded, onRefresh }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [transferTarget, setTransferTarget] = useState(null);
    const [poolDeleteTarget, setPoolDeleteTarget] = useState(null);
    const [memberRemoveTarget, setMemberRemoveTarget] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!orgId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/orgs/${orgId}`);
            setDetail(data);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load org details"));
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() => {
        if (expanded && orgId) load();
    }, [expanded, orgId, load]);

    const confirmRemoveMember = async () => {
        if (!memberRemoveTarget) return;
        setBusy(true);
        try {
            await api.delete(`/admin/orgs/${orgId}/members/${memberRemoveTarget.id}`);
            toast.success("Member removed");
            setMemberRemoveTarget(null);
            load();
            onRefresh?.();
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to remove member"));
        } finally {
            setBusy(false);
        }
    };

    const confirmDeletePool = async () => {
        if (!poolDeleteTarget) return;
        setBusy(true);
        try {
            await api.delete(`/admin/pools/${poolDeleteTarget.id}`);
            toast.success("Pool deleted");
            setPoolDeleteTarget(null);
            load();
            onRefresh?.();
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to delete pool"));
        } finally {
            setBusy(false);
        }
    };

    const transferOwnership = async () => {
        if (!transferTarget) return;
        setBusy(true);
        try {
            await api.post(`/admin/orgs/${orgId}/transfer-ownership`, { new_owner_user_id: transferTarget.id });
            toast.success(`Ownership transferred to ${transferTarget.email}`);
            setTransferTarget(null);
            load();
            onRefresh?.();
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to transfer ownership"));
        } finally {
            setBusy(false);
        }
    };

    const deletePool = (pool) => {
        setPoolDeleteTarget(pool);
    };

    if (!expanded) {
        return <div className="max-h-0 overflow-hidden border-t border-transparent" aria-hidden data-testid={`org-detail-collapsed-${orgId}`} />;
    }

    return (
        <>
            <div className={`relative border-t border-white/[0.04] bg-white/[0.015] ${DETAIL_PANEL_H} overflow-y-auto`}>
                <div className={loading ? "pointer-events-none invisible" : ""} aria-hidden={loading}>
                    {!detail ? (
                        <div className="px-4 py-3 text-xs text-white/40">No details</div>
                    ) : (
                    <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/[0.04]">
                        <div>
                            <p className="px-4 py-2 text-[10px] uppercase tracking-wider text-white/35 font-semibold inline-flex items-center gap-1.5">
                                <Users className="h-3 w-3" /> Members ({detail.members?.length || 0})
                            </p>
                            {!detail.members?.length ? (
                                <div className="px-4 pb-3 text-xs text-white/40">No members</div>
                            ) : (
                                <ul className="divide-y divide-white/[0.04]">
                                    {detail.members.map((m) => {
                                        const isOwner = m.org_role === "owner" || detail.org?.owner_id === m.id;
                                        return (
                                            <li key={m.id} className="px-4 py-2.5 flex items-center justify-between gap-2 text-sm">
                                                <div className="min-w-0">
                                                    <span className="text-white/85">{m.name}</span>
                                                    <span className="text-white/40 mx-1.5">·</span>
                                                    <span className="text-white/50 font-mono text-xs">{m.email}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    <span className="text-xs text-white/40 capitalize">{m.org_role}</span>
                                                    {!isOwner && (
                                                        <button
                                                            onClick={() => setMemberRemoveTarget(m)}
                                                            disabled={busy}
                                                            className="rounded p-1 text-rose-400 hover:bg-rose-500/10"
                                                            title="Remove member"
                                                            data-testid={`remove-member-${m.id}`}
                                                        >
                                                            <UserMinus className="h-3.5 w-3.5" />
                                                        </button>
                                                    )}
                                                    {!isOwner && (
                                                        <button
                                                            onClick={() => setTransferTarget(m)}
                                                            disabled={busy}
                                                            className="admin-accent-icon rounded p-1 hover:bg-[rgba(93,0,255,0.12)]"
                                                            title="Transfer ownership"
                                                            data-testid={`transfer-owner-${m.id}`}
                                                        >
                                                            <Crown className="h-3.5 w-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                        <div>
                            <p className="px-4 py-2 text-[10px] uppercase tracking-wider text-white/35 font-semibold inline-flex items-center gap-1.5">
                                <Layers className="h-3 w-3" /> Pools ({detail.pools?.length || 0})
                            </p>
                            {!detail.pools?.length ? (
                                <div className="px-4 pb-3 text-xs text-white/40">No pools</div>
                            ) : (
                                <ul className="divide-y divide-white/[0.04]">
                                    {detail.pools.map((p) => (
                                        <li key={p.id} className="px-4 py-2.5 flex items-center justify-between gap-2 text-sm">
                                            <div className="min-w-0">
                                                <span className="text-white/85">{p.name}</span>
                                                <span className="text-white/35 mx-1.5 text-xs capitalize">{p.status || "active"}</span>
                                                <p className="mt-0.5 text-[10px] text-white/35">Created {formatWhen(p.created_at)}</p>
                                            </div>
                                            <button
                                                onClick={() => deletePool(p)}
                                                disabled={busy}
                                                className="rounded p-1 text-rose-400 hover:bg-rose-500/10 shrink-0"
                                                title="Delete pool"
                                                data-testid={`delete-pool-${p.id}`}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    )}
                </div>
                {loading && (
                    <div className="absolute inset-0 grid place-items-center">
                        <div className="inline-flex items-center gap-2 text-xs text-white/40">
                            <Loader2 className="h-3 w-3 animate-spin" /> Loading details…
                        </div>
                    </div>
                )}
            </div>

            {memberRemoveTarget && (
                <Dialog
                    onClose={() => setMemberRemoveTarget(null)}
                    title="Remove member"
                    subtitle={`Remove ${memberRemoveTarget.email} from this organization?`}
                    testid="remove-member-dialog"
                >
                    <p className="text-sm text-white/55">They will be removed from the workspace and stripped from pool member lists where applicable.</p>
                    <DialogFooter>
                        <button onClick={() => setMemberRemoveTarget(null)} className="btn-ghost rounded-md px-4 py-2 text-sm">Cancel</button>
                        <button onClick={confirmRemoveMember} disabled={busy} className="rounded-md px-4 py-2 text-sm font-medium bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-40">
                            {busy ? "Removing…" : "Remove member"}
                        </button>
                    </DialogFooter>
                </Dialog>
            )}

            {poolDeleteTarget && (
                <DeleteConfirmModal
                    open={!!poolDeleteTarget}
                    onClose={() => setPoolDeleteTarget(null)}
                    onConfirm={confirmDeletePool}
                    busy={busy}
                    title={`Delete pool "${poolDeleteTarget.name}"?`}
                    description="Permanently removes this pool and its payouts, revenue events, and related data."
                >
                    <p className="text-xs text-white/45">Created {formatWhen(poolDeleteTarget.created_at)}</p>
                </DeleteConfirmModal>
            )}

            {transferTarget && (
                <Dialog
                    onClose={() => setTransferTarget(null)}
                    title="Transfer ownership"
                    subtitle={`Make ${transferTarget.email} the owner of ${detail?.org?.name || "this org"}?`}
                    testid="transfer-ownership-dialog"
                >
                    <p className="text-sm text-white/55">The current owner will be demoted to admin. This action is logged.</p>
                    <DialogFooter>
                        <button onClick={() => setTransferTarget(null)} className="btn-ghost rounded-md px-4 py-2 text-sm">Cancel</button>
                        <button onClick={transferOwnership} disabled={busy} className="btn-primary rounded-md px-4 py-2 text-sm inline-flex items-center gap-1.5">
                            <Crown className="h-3.5 w-3.5" /> {busy ? "Transferring…" : "Confirm transfer"}
                        </button>
                    </DialogFooter>
                </Dialog>
            )}
        </>
    );
}

export function AdminOrgsPage() {
    const [orgs, setOrgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteImpact, setDeleteImpact] = useState(null);
    const [impactLoading, setImpactLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/orgs");
            setOrgs(data.orgs || []);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load organizations"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openOrgDelete = async (org) => {
        setDeleteTarget(org);
        setDeleteImpact(null);
        setImpactLoading(true);
        try {
            const { data } = await api.get(`/admin/orgs/${org.id}`);
            setDeleteImpact(data);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load organization details"));
        } finally {
            setImpactLoading(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setBusy(true);
        try {
            const { data } = await api.delete(`/admin/orgs/${deleteTarget.id}`);
            const cascade = data.cascade || {};
            toast.success(`Organization deleted (${cascade.pools_deleted || 0} pools removed)`);
            setDeleteTarget(null);
            if (expandedId === deleteTarget.id) setExpandedId(null);
            load();
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to delete organization"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <AdminPanelShell
                title="Organizations"
                count={orgs.length}
                loading={loading}
                empty={!loading && orgs.length === 0}
                emptyMessage="No organizations yet."
                variant="list"
                testId="admin-orgs-panel"
            >
                <p className="px-4 sm:px-5 py-2.5 text-xs text-white/40 border-b border-white/[0.04]">
                    Subscription tier is per workspace (pool limits and features). Change it from the Users tab — it applies to the owner and syncs here.
                </p>
                <div className="divide-y divide-white/[0.04]">
                    {orgs.map((org) => {
                        const expanded = expandedId === org.id;
                        const planClass = PLAN_BADGE[org.plan] || PLAN_BADGE.free;
                        return (
                            <div key={org.id} data-testid={`admin-org-${org.id}`}>
                                <div className="px-4 sm:px-5 py-3.5 sm:py-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-3 hover:bg-white/[0.015] transition-colors lg:min-h-[72px]">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2 lg:block">
                                            <p className="font-medium text-white truncate">{org.name}</p>
                                            <span
                                                className={`lg:hidden inline-flex shrink-0 px-2 py-0.5 rounded-md text-xs font-medium border capitalize ${planClass}`}
                                                title="Workspace subscription tier"
                                                data-testid={`org-plan-badge-mobile-${org.id}`}
                                            >
                                                {org.plan || "free"}
                                            </span>
                                        </div>
                                        <div className="mt-1 space-y-0.5 text-xs text-white/40">
                                            <p className="font-mono truncate" title={org.id}>
                                                <span className="lg:hidden">{formatOrgId(org.id, { compact: true })}</span>
                                                <span className="hidden lg:inline">{org.id}</span>
                                            </p>
                                            <p className="truncate">
                                                {org.created_at && <span>Created {formatWhen(org.created_at)}</span>}
                                                {org.owner_email && (
                                                    <>
                                                        {org.created_at ? <span className="mx-1.5">·</span> : null}
                                                        <span>Owner: {org.owner_name || org.owner_email}</span>
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 lg:justify-end lg:gap-4 shrink-0 w-full lg:w-auto">
                                        <div className="flex items-center gap-3 text-xs text-white/45">
                                            <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {org.member_count ?? 0}</span>
                                            <span>{org.pool_count ?? 0} pools</span>
                                            <span
                                                className={`hidden lg:inline-flex px-2 py-0.5 rounded-md text-xs font-medium border capitalize ${planClass}`}
                                                title="Workspace subscription tier (free / pro / business)"
                                                data-testid={`org-plan-badge-${org.id}`}
                                            >
                                                {org.plan || "free"}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <button
                                                onClick={() => setExpandedId(expanded ? null : org.id)}
                                                className="btn-ghost rounded-md px-2 py-1 text-xs inline-flex items-center gap-1"
                                                data-testid={`view-org-${org.id}`}
                                            >
                                                Details {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                            </button>
                                            <button
                                                onClick={() => openOrgDelete(org)}
                                                className="rounded-md px-2 py-1 text-xs inline-flex items-center gap-1 text-rose-400 hover:bg-rose-500/10 transition-colors"
                                                data-testid={`delete-org-${org.id}`}
                                            >
                                                <Trash2 className="h-3 w-3" /> Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <OrgDetailPanel orgId={org.id} expanded={expanded} onRefresh={load} />
                            </div>
                        );
                    })}
                </div>
            </AdminPanelShell>

            <DeleteConfirmModal
                open={!!deleteTarget}
                onClose={() => { setDeleteTarget(null); setDeleteImpact(null); }}
                onConfirm={confirmDelete}
                busy={busy}
                title={`Delete ${deleteTarget?.name}?`}
                description="Permanently removes the organization, all pools, payouts, memberships, and invitations."
            >
                {impactLoading && <p className="text-xs text-white/45">Loading organization details…</p>}
                {deleteImpact && (
                    <div className="rounded-lg border border-rose-500/20 bg-rose-500/[0.05] p-3 space-y-2 text-xs text-white/60">
                        <p>Organization created {formatWhen(deleteImpact.org?.created_at)}</p>
                        <p>{deleteImpact.pools?.length || 0} pool(s) will be deleted:</p>
                        {deleteImpact.pools?.length > 0 ? (
                            <ul className="space-y-1 max-h-32 overflow-auto">
                                {deleteImpact.pools.map((pool) => (
                                    <li key={pool.id} className="flex justify-between gap-2">
                                        <span className="truncate text-white/75">{pool.name}</span>
                                        <span className="shrink-0 text-white/40">{formatWhen(pool.created_at)}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-white/40">No pools in this organization.</p>
                        )}
                    </div>
                )}
            </DeleteConfirmModal>
        </>
    );
}
